import React, { useState, type ReactNode } from 'react';
import { useLocale } from '@/hooks/useLocale';

export interface TextFieldProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  label?: string;
  title?: string;
  disabled?: boolean;
  compact?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** Keep a joined address prefix and editable value in the same order. */
  contentDirection?: 'ltr' | 'rtl';
  type?: 'text' | 'email' | 'url' | 'search' | 'tel' | 'password';
  autoComplete?: string;
  autoFocus?: boolean;
  onSubmit?: () => void;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: 'var(--tabbio-text-tertiary)',
  marginBottom: 6,
  display: 'block',
};

const WRAPPER_BASE: React.CSSProperties = {
  width: '100%',
  borderRadius: 9999,
  border: '1px solid var(--tabbio-border)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  backgroundColor: 'var(--tabbio-bg)',
  transition: 'border-color 150ms, background-color 150ms, opacity 150ms',
  boxSizing: 'border-box',
};

const WRAPPER_DEFAULT: React.CSSProperties = {
  ...WRAPPER_BASE,
  minHeight: 50,
  padding: '0 20px',
};

const WRAPPER_COMPACT: React.CSSProperties = {
  ...WRAPPER_BASE,
  minHeight: 40,
  padding: '0 14px',
};

const INPUT_BASE: React.CSSProperties = {
  flex: 1,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: 'var(--tabbio-text)',
  fontFamily: 'inherit',
  padding: 0,
  minWidth: 0,
};

export function TextField({
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  compact = false,
  iconLeft,
  iconRight,
  contentDirection,
  type = 'text',
  autoComplete,
  autoFocus,
  onSubmit,
  multiline = false,
  minRows = 3,
  maxRows = 8,
}: TextFieldProps) {
  const { isRTL } = useLocale();
  const [focused, setFocused] = useState(false);
  const wrapperBase = compact ? WRAPPER_COMPACT : WRAPPER_DEFAULT;
  const fontSize = compact ? 14 : 16;

  // Multiline uses a textarea: non-pill shape
  if (multiline) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'}>
        {label ? <label style={LABEL_STYLE}>{label}</label> : null}
        <div
          dir={contentDirection}
          style={{
            ...wrapperBase,
            borderRadius: 16,
            paddingBlock: compact ? 10 : 14,
            paddingInline: compact ? 14 : 18,
            alignItems: 'flex-start',
            opacity: disabled ? 0.5 : 1,
            borderColor: focused ? 'var(--tabbio-text-secondary)' : 'var(--tabbio-border)',
          }}
        >
          <textarea
            dir={contentDirection ?? 'auto'}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            rows={minRows}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              ...INPUT_BASE,
              fontSize,
              lineHeight: '20px',
              resize: 'vertical',
              minHeight: minRows * 20,
              maxHeight: maxRows * 20,
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      {label ? <label style={LABEL_STYLE}>{label}</label> : null}
      <div
        dir={contentDirection}
        style={{
          ...wrapperBase,
          opacity: disabled ? 0.5 : 1,
          borderColor: focused ? 'var(--tabbio-text-secondary)' : 'var(--tabbio-border)',
        }}
      >
        {iconLeft}
        <input
          dir={contentDirection ?? (['email', 'url', 'tel', 'password'].includes(type) ? 'ltr' : 'auto')}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && onSubmit) {
              event.preventDefault();
              onSubmit();
            }
          }}
          style={{
            ...INPUT_BASE,
            fontSize,
          }}
        />
        {iconRight}
      </div>
    </div>
  );
}
